/**
 * colormaps.ts
 *
 * Single source of truth for every colormap used in the app.
 * Mixes the standard d3-scale-chromatic maps with our own custom ones,
 * all exposed under one `colormaps` object with the same call shape:
 *
 *   colormaps[name](t)  // t in [0, 1] -> "#rrggbb"
 *
 * Usage:
 *   import { colormaps } from './colormaps';
 *   const hex = colormaps['plasma'](0.5);
 *   const hex2 = colormaps['signal'](0.5);
 */

import {
  interpolateInferno,
  interpolatePlasma,
  interpolateViridis,
} from 'd3-scale-chromatic';

// ---------------------------------------------------------------------------
// Shared type: every colormap, d3-provided or custom, has this exact shape.
// ---------------------------------------------------------------------------

export type ColormapFn = (t: number) => string;

// ---------------------------------------------------------------------------
// Helpers for building custom maps from a handful of RGB control points
// instead of hand-written 100-300 entry lookup tables.
// ---------------------------------------------------------------------------

type RGB = [number, number, number];

const toHex = (n: number): string =>
  Math.round(n).toString(16).padStart(2, '0');

const rgbToHex = ([r, g, b]: RGB): string =>
  `#${toHex(r)}${toHex(g)}${toHex(b)}`;

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/**
 * Build a d3-style interpolator from an ordered list of RGB stops.
 *
 * @param colors    control point colors (RGB 0-255), in order along the scale
 * @param positions where each color sits on [0, 1]. Defaults to evenly spaced.
 */
export function createInterpolator(
  colors: RGB[],
  positions?: number[],
): ColormapFn {
  const n = colors.length;
  if (n < 2) throw new Error('createInterpolator needs at least 2 colors');

  const pos = positions ?? colors.map((_, i) => i / (n - 1));

  return function interpolate(t: number): string {
    const clamped = Math.max(0, Math.min(1, t));

    // find the segment [pos[i], pos[i+1]] containing `clamped`
    let i = 0;
    while (i < n - 2 && clamped > pos[i + 1]) i++;

    const segStart = pos[i];
    const segEnd = pos[i + 1];
    const localT
      = segEnd === segStart ? 0 : (clamped - segStart) / (segEnd - segStart);

    const c0 = colors[i];
    const c1 = colors[i + 1];
    const rgb: RGB = [
      lerp(c0[0], c1[0], localT),
      lerp(c0[1], c1[1], localT),
      lerp(c0[2], c1[2], localT),
    ];

    return rgbToHex(rgb);
  };
}

// ---------------------------------------------------------------------------
// Custom colormaps
// ---------------------------------------------------------------------------

/**
 * "signal" — reconstructed from the original signalIntensity lookup table.
 * It's a straight 3-stop blend: blue -> gold -> red.
 */
// const interpolateSignal: ColormapFn = createInterpolator([
//   [0, 114, 189], // blue
//   [237, 177, 32], // gold
//   [255, 0, 0], // red
// ]);

/**
 * "signalClamped" — same 3 colors, but pushed toward the middle of the
 * range so the ends stay flat for longer, matching the saturation
 * behavior visible in the original lookup table (~27% flat on each end).
 */
const interpolateSignalClamped: ColormapFn = createInterpolator(
  [
    [0, 114, 189],
    [0, 114, 189],
    [237, 177, 32],
    [255, 0, 0],
    [255, 0, 0],
  ],
  [0, 0.27, 0.5, 0.73, 1],
);

/**
 * "parula" — MATLAB's default colormap, dark blue -> teal/green -> yellow.
 * Reconstructed from MATLAB's published 64-entry parula RGB table.
 */
const interpolateParula: ColormapFn = createInterpolator([
  [53, 42, 135],
  [54, 48, 147],
  [54, 55, 160],
  [53, 61, 173],
  [50, 67, 186],
  [44, 74, 199],
  [32, 83, 212],
  [15, 92, 221],
  [3, 99, 225],
  [2, 104, 225],
  [4, 109, 224],
  [8, 113, 222],
  [13, 117, 220],
  [16, 121, 218],
  [18, 125, 216],
  [20, 129, 214],
  [20, 133, 212],
  [19, 137, 211],
  [16, 142, 210],
  [12, 147, 210],
  [9, 152, 209],
  [7, 156, 208],
  [6, 160, 205],
  [6, 164, 202],
  [6, 167, 198],
  [7, 169, 194],
  [10, 172, 190],
  [15, 174, 185],
  [21, 177, 180],
  [29, 179, 175],
  [37, 181, 169],
  [46, 183, 164],
  [56, 185, 158],
  [66, 187, 152],
  [77, 188, 146],
  [89, 189, 140],
  [101, 190, 134],
  [113, 191, 128],
  [124, 191, 123],
  [135, 191, 119],
  [146, 191, 115],
  [156, 191, 111],
  [165, 190, 107],
  [174, 190, 103],
  [183, 189, 100],
  [192, 188, 96],
  [200, 188, 93],
  [209, 187, 89],
  [217, 186, 86],
  [225, 186, 82],
  [233, 185, 78],
  [241, 185, 74],
  [248, 186, 68],
  [253, 190, 61],
  [255, 195, 55],
  [254, 200, 50],
  [252, 206, 46],
  [250, 211, 42],
  [247, 216, 38],
  [245, 222, 33],
  [244, 228, 29],
  [245, 235, 24],
  [246, 243, 19],
  [249, 251, 14],
]);

// ---------------------------------------------------------------------------
// Combined export — drop-in for `colormaps[name](value)`
// ---------------------------------------------------------------------------

export const colormaps: Record<string, ColormapFn> = {
  // d3-scale-chromatic
  plasma: interpolatePlasma,
  viridis: interpolateViridis,
  inferno: interpolateInferno,

  // custom
  // signal: interpolateSignal,
  bmoreSignal: interpolateSignalClamped,
  parula: interpolateParula,
};
